import {
  Box,
  Button,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Tag,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  createPrismaQuery,
  createPrismaSort,
  DataTable,
  DataTableAddFilter,
  DataTableFilter,
  DataTableFilters,
  DataTableSearch,
  useCrud,
} from '@pelicargo/datatable';
import { Show } from '@pelicargo/ui';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { HiEllipsisVertical } from 'react-icons/hi2';

import { ProviderFormDrawer } from '../components/Drawers/AirlineFormDrawer';
import { ProviderOutput } from '../hooks/providers/useForm';
import { useProviders } from '../hooks/providers/useProviders';

const providerFilters = [
  {
    name: 'is_active',
    label: 'Status',
    type: 'boolean',
    inputType: 'radio',
    operator: 'equals',
    value: undefined,
    // TODO: refactor booleans later
    options: [
      {
        label: 'Active',
        value: true,
      },
      {
        label: 'Inactive',
        value: false,
      },
    ],
  },
] satisfies DataTableFilter[];

type TableColumns = {
  name: string;
  id: string;
  awb_prefix: string;
  is_active: boolean;
  action?: any;
};

export const ProvidersScreen = () => {
  const providerDrawer = useDisclosure();

  const { query, handleQueryChange, filters, setFilters, sorting, setSorting, pagination, setPagination, take, skip } =
    useCrud();

  const [selectedProvider, setSelectedProvider] = useState<Partial<ProviderOutput>>();

  const {
    data,
    count = 0,
    refetch,
  } = useProviders({
    search: query,
    skip,
    take,
    orderBy: createPrismaSort(sorting),
    filters: createPrismaQuery(filters),
  });

  const handleRowClick = useCallback(
    (row: TableColumns) => {
      setSelectedProvider(row);
      providerDrawer.onOpen();
    },
    [providerDrawer],
  );

  const handleAddFilter = (filter: DataTableFilter) => () => {
    setFilters((prevFilters) => [...prevFilters, filter]);
  };

  const handleClear = () => {
    setFilters([]);
  };

  const handleClose = () => {
    setSelectedProvider({});
    refetch();
    providerDrawer.onClose();
  };

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'Identifier',
        cell: (cell) => <Text>{cell.renderValue()}</Text>,
      }),
      columnHelper.accessor('name', {
        header: 'Provider Name',
        size: Number.MAX_SAFE_INTEGER,
        cell: (cell) => <Text>{cell.renderValue()}</Text>,
      }),
      columnHelper.accessor('awb_prefix', {
        header: 'AWB',
        cell: (cell) => <Text>{cell.renderValue()}</Text>,
      }),
      columnHelper.accessor('is_active', {
        header: 'Status',
        cell: (cell) => {
          const isActive = cell.getValue();
          const label = isActive ? 'Active' : 'Inactive';
          const color = isActive ? 'green' : 'red';
          return (
            <Tag size="md" colorScheme={color}>
              {label}
            </Tag>
          );
        },
      }),
      columnHelper.accessor('action', {
        header: '',
        cell: (cell) => {
          const row = cell.row.original;
          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <Menu size="xs">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  size="sm"
                  icon={<HiEllipsisVertical />}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItem onClick={() => handleRowClick(row)}>Update</MenuItem>
                </MenuList>
              </Menu>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, handleRowClick],
  );

  return (
    <>
      <Stack spacing="0" h="full">
        <Stack p="4" spacing="4">
          <Stack direction="row" justify="space-between" zIndex="99" w="full">
            <Stack direction="row" align="center" w="full">
              <Heading variant="h3">Providers</Heading>
              <DataTableSearch value={query} onChange={handleQueryChange} />
              <DataTableAddFilter
                activeFilters={filters?.map((filter) => filter.name)}
                options={providerFilters}
                handleAddFilter={handleAddFilter}
              />
            </Stack>
            <Button colorScheme="brand" onClick={providerDrawer.onOpen}>
              Create Provider
            </Button>
          </Stack>
          <Show if={filters.length > 0}>
            <DataTableFilters filters={filters} setFilters={setFilters} onClear={handleClear} />
          </Show>
        </Stack>
        <DataTable
          columns={columns}
          data={data ?? []}
          dataCount={count ?? 0}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          onRowClick={handleRowClick}
        />
      </Stack>
      <ProviderFormDrawer providerId={selectedProvider?.id} isOpen={providerDrawer.isOpen} onClose={handleClose} />
    </>
  );
};
